/**
 * Coverage waitlist form — anyone (no auth) can drop email + ZIP to be pinged
 * when a specialized sitter is available in their area.
 * Used on the landing page and as the empty-state on /search.
 */
import React, { useState } from "react";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MailCheck, Bell } from "lucide-react";

export default function CoverageWaitlistForm({ source = "landing", compact = false }) {
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !zip.trim()) {
      toast.error("Please enter both email and ZIP code");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/waitlist/coverage", {
        email: email.trim(),
        zip_code: zip.trim(),
        name: name.trim(),
        notes: notes.trim(),
        source,
      });
      setDone(true);
      if (data.status === "already_on_list") {
        toast.success("You're already on our list — we'll be in touch.");
      } else {
        toast.success("You're on the list!");
      }
    } catch (err) { toast.error(extractError(err)); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="paper-card p-6 sm:p-8 bg-sage-50 border-sage-200" data-testid="waitlist-success">
        <div className="inline-flex items-center gap-2 text-sage-700 font-heading font-bold text-lg">
          <MailCheck className="w-5 h-5" /> You're on the list
        </div>
        <p className="mt-2 text-sm text-stone2-700 leading-relaxed">
          We'll email <strong>{email}</strong> the moment a verified specialized sitter is available in ZIP <strong>{zip}</strong>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`${compact ? "" : "paper-card p-6 sm:p-8"} space-y-3`} data-testid="waitlist-form">
      {!compact && (
        <>
          <div className="inline-flex items-center gap-2 text-ochre-700 font-heading font-bold text-lg">
            <Bell className="w-5 h-5" /> Join the coverage waitlist
          </div>
          <p className="text-sm text-stone2-600 leading-relaxed">
            No verified sitters in your area yet? Drop your ZIP and email — we'll ping you the moment coverage arrives.
          </p>
        </>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          data-testid="waitlist-email"
          className="rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500"
        />
        <input
          type="text"
          required
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/[^\d\-]/g, ""))}
          placeholder="ZIP code"
          maxLength={10}
          data-testid="waitlist-zip"
          className="rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500"
        />
      </div>

      {!compact && (
        <>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            data-testid="waitlist-name"
            className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500"
          />
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any specifics? Age range, days you need coverage, etc. (optional)"
            rows={2}
            maxLength={500}
            data-testid="waitlist-notes"
            className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500"
          />
        </>
      )}

      <button
        type="submit"
        disabled={loading}
        data-testid="waitlist-submit"
        className="btn-primary w-full justify-center"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
        Notify me when a sitter joins
      </button>
      <p className="text-[11px] text-stone2-500 text-center">
        We'll only email you about your area — no spam, no sharing. Unsubscribe anytime.
      </p>
    </form>
  );
}
